import React, { useMemo, useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useAuth } from '../context/useAuth';
import { LoadingSpinner } from '../components/Common';
import { DynamicIcon } from '../components/icons';
import { apiFetch } from '../api';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';

// ============================================
// UI Components
// ============================================

const Button = ({ children, variant = 'default', size = 'default', onClick, className = '', disabled = false }) => {
    const baseClasses = 'inline-flex items-center justify-center rounded-md font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background';
    const variants = {
        default: 'bg-primary text-white hover:opacity-90 shadow-md hover:shadow-lg',
        outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400',
        destructive: 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 shadow-md hover:shadow-lg',
        secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200',
        ghost: 'hover:bg-gray-100 text-gray-700'
    };
    const sizes = {
        default: 'h-10 py-2 px-4',
        sm: 'h-8 px-3 text-sm',
        lg: 'h-11 px-8',
        icon: 'h-9 w-9 p-0'
    };
    
    return (
        <button 
            className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
            onClick={onClick}
            disabled={disabled}
        >
            {children}
        </button>
    );
};

const Input = ({ placeholder, value, onChange, className = '', id }) => (
    <input 
        id={id}
        type="text" 
        className={`flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ${className}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
    />
);

const Textarea = ({ placeholder, value, onChange, className = '', id, rows = 3 }) => (
    <textarea 
        id={id}
        rows={rows}
        className={`flex w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ${className}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
    />
);

const Label = ({ htmlFor, children, className = '' }) => (
    <label 
        htmlFor={htmlFor}
        className={`text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block ${className}`}
    >
        {children}
    </label>
);

const Badge = ({ variant = 'default', children, className = '' }) => {
    const variants = {
        default: 'bg-primary/10 text-primary border border-primary/20',
        secondary: 'bg-gray-100 text-gray-800 border border-gray-200',
        destructive: 'bg-red-100 text-red-800 border border-red-200',
        outline: 'border border-gray-300 text-gray-700',
        success: 'bg-green-100 text-green-800 border border-green-200'
    };
    
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${variants[variant]} ${className}`}>
            {children}
        </span>
    );
};

const Card = ({ children, className = '', onClick }) => (
    <div onClick={onClick} className={`rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-200 ${className}`}>
        {children}
    </div>
);

const CardHeader = ({ children, className = '' }) => (
    <div className={`p-6 border-b border-gray-100 ${className}`}>
        {children}
    </div>
);

const CardTitle = ({ children, className = '' }) => (
    <h3 className={`text-xl font-bold tracking-tight text-gray-900 ${className}`}>
        {children}
    </h3>
);

const CardContent = ({ children, className = '' }) => (
    <div className={`p-6 ${className}`}>
        {children}
    </div>
);

const Table = ({ children }) => (
    <div className="w-full overflow-auto rounded-lg border border-gray-200">
        <table className="w-full caption-bottom text-sm">
            {children}
        </table>
    </div>
);

const TableHeader = ({ children }) => (
    <thead className="bg-gray-50">
        {children}
    </thead>
);

const TableBody = ({ children }) => (
    <tbody className="divide-y divide-gray-200">
        {children}
    </tbody>
);

const TableRow = ({ children, className = '' }) => (
    <tr className={`transition-colors hover:bg-gray-50 ${className}`}>
        {children}
    </tr>
);

const TableHead = ({ children, className = '' }) => (
    <th className="h-12 px-4 text-left align-middle font-semibold text-gray-700 border-b border-gray-200">
        {children}
    </th>
);

const TableCell = ({ children, className = '' }) => (
    <td className={`p-4 align-middle ${className}`}>
        {children}
    </td>
);

const Tabs = ({ value, onValueChange, children }) => {
    // Find TabsList and TabsContent among children
    let tabsList = null;
    const tabsContents = [];
    
    React.Children.forEach(children, child => {
        if (!child) return;
        // Check by displayName or component name
        const name = child.type?.displayName || child.type?.name || '';
        if (name === 'TabsList') {
            tabsList = child;
        } else if (name === 'TabsContent') {
            tabsContents.push(child);
        }
    });
    
    return (
        <div className="w-full">
            {tabsList && React.cloneElement(tabsList, { value, onValueChange })}
            {tabsContents.map(content => 
                content.props.value === value ? content : null
            )}
        </div>
    );
};


const TabsList = ({ value, onValueChange, children }) => (
    <div className="inline-flex h-11 items-center justify-center rounded-lg bg-gray-100 p-1.5 text-gray-500 mb-6">
        {React.Children.map(children, child => 
            React.cloneElement(child, { 
                onValueChange,
                isActive: child.props.value === value 
            })
        )}
    </div>
);
// Set displayName for reliable identification
TabsList.displayName = 'TabsList';

const TabsTrigger = ({ value: tabValue, onValueChange, children, isActive }) => (
    <button
        type="button"
        onClick={() => onValueChange(tabValue)}
        className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${
            isActive 
                ? 'bg-white text-primary shadow-sm' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
        }`}
    >
        {children}
    </button>
);

const TabsContent = ({ children, value, className = '' }) => (
    <div className={`animate-in fade-in-50 duration-200 ${className}`}>
        {children}
    </div>
);
TabsContent.displayName = 'TabsContent';

const Select = ({ value, onValueChange, children, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = React.useRef(null);
    
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200"
            >
                <span>{value}</span>
                <DynamicIcon name="ChevronDown" className={`h-4 w-4 opacity-50 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div className="absolute top-full z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                    {React.Children.map(children, child => 
                        React.cloneElement(child, { 
                            onSelect: (val) => {
                                onValueChange(val);
                                setIsOpen(false);
                            }
                        })
                    )}
                </div>
            )}
        </div>
    );
};


const SelectTrigger = ({ children }) => <>{children}</>;

const SelectValue = ({ children }) => <>{children}</>;

const SelectContent = ({ children }) => <>{children}</>;

const SelectItem = ({ value: itemValue, children, onSelect }) => (
    <button
        type="button"
        onClick={() => onSelect(itemValue)}
        className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 px-3 text-sm outline-none hover:bg-primary/10 focus:bg-primary/10 text-gray-700 transition-colors"
    >
        {children}
    </button>
);


const Dropdown = ({ trigger, children, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = React.useRef(null);
    
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    return (
        <div className={`relative ${className}`} ref={containerRef}>
            {React.cloneElement(trigger, { onClick: () => setIsOpen(!isOpen) })}
            {isOpen && (
                <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-right">
                    {React.Children.map(children, child => 
                        child && React.cloneElement(child, { 
                            onClick: (e) => {
                                child.props.onClick?.(e);
                                setIsOpen(false);
                            }
                        })
                    )}
                </div>
            )}
        </div>
    );
};

const DropdownItem = ({ children, onClick, className = '', disabled = false }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={`flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:pointer-events-none ${className}`}
    >
        {children}
    </button>
);

// ============================================
// Dialog Components
// ============================================

const Dialog = ({ open, onOpenChange, children, panelClassName }) => {
    if (!open) return null;

    const panelClasses = panelClassName ?? 'max-w-2xl';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => onOpenChange(false)} />
            <div
                className={`relative z-50 w-full rounded-xl bg-white shadow-2xl animate-in fade-in-50 zoom-in-95 duration-200 ${panelClasses}`}
            >
                {children}
            </div>
        </div>
    );
};


const DialogContent = ({ children, className = '' }) => (
    <div className={cn('p-6', className)}>{children}</div>
);

const DialogHeader = ({ children, className = '' }) => (
    <div className={cn('mb-6', className)}>{children}</div>
);

const DialogTitle = ({ children }) => (
    <h2 className="text-xl font-bold tracking-tight text-gray-900">
        {children}
    </h2>
);

const DialogDescription = ({ children }) => (
    <p className="text-sm text-gray-500 mt-1.5">
        {children}
    </p>
);

// ============================================
// Delete Confirmation Modal
// ============================================

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, itemName, itemType }) => {
    if (!isOpen) return null;
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} />
            <div className="relative z-50 w-full max-w-md rounded-xl bg-white shadow-2xl animate-in fade-in-50 zoom-in-95 duration-200">
                <div className="p-6">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                                <DynamicIcon name="AlertTriangle" className="w-6 h-6 text-red-600" />
                            </div>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-1">
                                Delete {itemType}
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">
                                Are you sure you want to delete <strong className="text-gray-700">"{itemName}"</strong>? 
                                This action cannot be undone and may affect any dependent items.
                            </p>
                            <div className="flex gap-3">
                                <Button variant="outline" onClick={onClose} className="flex-1">
                                    Cancel
                                </Button>
                                <Button variant="destructive" onClick={onConfirm} className="flex-1">
                                    <DynamicIcon name="Trash2" className="w-4 h-4 mr-2" />
                                    Delete
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


// ============================================
// Edit Modal
// ============================================

const EditModal = ({ isOpen, onClose, onSave, item, itemType, domains }) => {
    const [formData, setFormData] = useState({});
    
    useEffect(() => {
        if (item) {
            setFormData({ ...item });
        }
    }, [item]);
    
    if (!isOpen || !item) return null;
    
    const handleSave = () => {
        onSave(itemType, item.id, formData);
        onClose();
    };
    
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Edit {itemType.charAt(0).toUpperCase() + itemType.slice(1)}</DialogTitle>
                    <DialogDescription>
                        Make changes to your {itemType} below.
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="edit-name">Name</Label>
                        <Input
                            id="edit-name"
                            value={formData.name || ''}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="Enter name"
                        />
                    </div>
                    
                    <div>
                        <Label htmlFor="edit-description">Description</Label>
                        <Textarea
                            id="edit-description"
                            value={formData.description || ''}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                            placeholder="Enter description"
                            rows={3}
                        />
                    </div>
                    
                    {itemType === 'domain' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="edit-status">Status</Label>
                                <Select 
                                    value={formData.status || 'active'} 
                                    onValueChange={(value) => setFormData({...formData, status: value})}
                                >
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="inactive">Inactive</SelectItem>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="edit-version">Version</Label>
                                <Input
                                    id="edit-version"
                                    value={formData.version || '1.0'}
                                    onChange={(e) => setFormData({...formData, version: e.target.value})}
                                />
                            </div>
                        </div>
                    )}
                    

                    
                    {itemType === 'metric' && (
                        <div>
                            <Label htmlFor="edit-definition">Definition</Label>
                            <Textarea
                                id="edit-definition"
                                value={formData.definition || ''}
                                onChange={(e) => setFormData({...formData, definition: e.target.value})}
                                placeholder="Enter metric definition"
                                rows={3}
                            />
                        </div>
                    )}
                    
                    {itemType === 'rule' && (
                        <>
                            <div>
                                <Label htmlFor="edit-rule">Rule</Label>
                                <Textarea
                                    id="edit-rule"
                                    value={formData.rule || ''}
                                    onChange={(e) => setFormData({...formData, rule: e.target.value})}
                                    placeholder="Enter rule definition"
                                    rows={3}
                                />
                            </div>
                            <div>
                                <Label htmlFor="edit-priority">Priority</Label>
                                <Select 
                                    value={formData.priority || 'medium'} 
                                    onValueChange={(value) => setFormData({...formData, priority: value})}
                                >
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                </Select>
                            </div>
                        </>
                    )}
                    
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave}>
                            <DynamicIcon name="Save" className="w-4 h-4 mr-2" />
                            Save Changes
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// ============================================
// Alert Components
// ============================================

const Alert = ({ variant = 'default', children, className = '' }) => {
    const variants = {
        default: 'bg-primary/5 border-primary/20 text-primary',
        destructive: 'bg-red-50 border-red-200 text-red-800',
        success: 'bg-green-50 border-green-200 text-green-800'
    };
    
    return (
        <div className={`rounded-lg border p-4 ${variants[variant]} ${className}`}>
            {children}
        </div>
    );
};


const AlertDescription = ({ children }) => (
    <div className="text-sm flex items-center gap-2">
        {children}
    </div>
);

// ============================================
// Main Component
// ============================================


// ============================================
// Document Import Modal
// ============================================

const DocumentImportModal = ({ isOpen, onClose, domains, onSuccess, token, overrideSelectedDomain, onDownloadTemplate }) => {
    const [step, setStep] = useState(1);
    const [file, setFile] = useState(null);
    const [selectedDomain, setSelectedDomain] = useState('');
    const [uploading, setUploading] = useState(false);
    const [extracted, setExtracted] = useState(null);
    const [error, setError] = useState(null);
    const [skippedDuplicates, setSkippedDuplicates] = useState(0);

    // Reset when opened
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setFile(null);
            setSelectedDomain(overrideSelectedDomain?.toString() || '');
            setExtracted(null);
            setError(null);
            setSkippedDuplicates(0);
        }
    }, [isOpen, overrideSelectedDomain]);

    if (!isOpen) return null;

    const handleUpload = async () => {
        if (!file || !selectedDomain) {
            setError("Please select a file and a domain.");
            return;
        }

        setUploading(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            console.log(`[DocImport] Uploading '${file.name}' to domain ${selectedDomain}`);
            // Upload uses interactive layer (port 8001) — falls back to control plane (port 8000)
            const interactiveBase = (import.meta.env.VITE_INTERACTIVE_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1').replace(/\/+$/, '');
            const resp = await fetch(`${interactiveBase}/semantic-kb/domains/${selectedDomain}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({ detail: resp.statusText }));
                throw new Error(err.detail || 'Upload failed');
            }
            const data = await resp.json();

            console.log('[DocImport] Extraction successful', data);
            setExtracted(data);
            setSkippedDuplicates(data.skipped_duplicates || 0);
            setStep(2);
        } catch (err) {
            const msg = err.message || 'Upload failed. Please try again.';
            console.error('[DocImport] Upload error:', err);
            setError(msg);
        } finally {
            setUploading(false);
        }
    };

    const handleConfirm = async () => {
        setUploading(true);
        setError(null);
        try {
            const payload = {
                s3_key: extracted.s3_key,
                filename: extracted.filename,
                extracted: extracted.extracted,
                file_type: file?.name.split('.').pop() || '',
                row_count: extracted.row_count,
                truncated: extracted.truncated
            };

            console.log('[DocImport] Confirming import', payload);
            // confirm-import writes to DB only — stays on control plane via apiFetch
            const result = await apiFetch(`/semantic-kb/domains/${selectedDomain}/confirm-import`, token, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const parts = [];
            if (result.entities_added > 0) parts.push(`${result.entities_added} entities`);
            if (result.metrics_added  > 0) parts.push(`${result.metrics_added} metrics`);
            if (result.rules_added    > 0) parts.push(`${result.rules_added} rules`);
            const summary = parts.length > 0 ? `Imported: ${parts.join(', ')}.` : 'No new items imported.';
            const dupNote = result.skipped_duplicates > 0 ? ` Skipped ${result.skipped_duplicates} duplicates.` : '';

            onSuccess(summary + dupNote);
            onClose();
        } catch (err) {
            const msg = err.message || 'Confirmation failed. Please try again.';
            console.error('[DocImport] Confirm error:', err);
            setError(msg);
        } finally {
            setUploading(false);
        }
    };

    const renderStep1 = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* LEFT COLUMN: Context/Templates (5 cols) */}
                <div className="md:col-span-5 space-y-4">
                    <div className="p-4 rounded-xl border border-primary/20 bg-gradient-to-b from-primary/5 to-white shadow-sm">
                        <h4 className="font-semibold text-primary/90 mb-2 flex items-center gap-2 text-sm">
                            <div className="p-1.5 bg-primary/10 text-primary rounded-md">
                                <DynamicIcon name="FileText" className="w-4 h-4" /> 
                            </div>
                            Upload via Template
                        </h4>
                        <p className="text-xs text-slate-600 leading-relaxed mb-4">
                            For precise mapping, download our Excel templates and bulk-import your specific metrics and business rules directly.
                        </p>
                        <div className="flex flex-col gap-2">
                            <Button variant="outline" size="sm" className="w-full justify-start h-9 text-xs font-medium border-primary/20 hover:bg-primary/5 hover:text-primary hover:border-primary/30 transition-colors" onClick={(e) => { e.preventDefault(); onDownloadTemplate('metrics'); }}>
                                <DynamicIcon name="Download" className="w-3.5 h-3.5 mr-2" /> Metrics Template
                            </Button>
                            <Button variant="outline" size="sm" className="w-full justify-start h-9 text-xs font-medium border-primary/20 hover:bg-primary/5 hover:text-primary hover:border-primary/30 transition-colors" onClick={(e) => { e.preventDefault(); onDownloadTemplate('rules'); }}>
                                <DynamicIcon name="Download" className="w-3.5 h-3.5 mr-2" /> Rules Template
                            </Button>
                        </div>
                    </div>

                    <div className="p-4 rounded-xl border border-primary/20 bg-gradient-to-b from-primary/5 to-white shadow-sm">
                        <h4 className="font-semibold text-primary mb-2 flex items-center gap-2 text-sm">
                            <div className="p-1.5 bg-primary/10 text-primary rounded-md">
                                <DynamicIcon name="Sparkles" className="w-4 h-4" /> 
                            </div>
                            Use AI to Extract
                        </h4>
                        <p className="text-xs text-slate-600 leading-relaxed">
                            Upload unstructured files (PDF, DOCX) like data dictionaries. Context AI will automatically parse and discover semantic knowledge.
                        </p>
                    </div>
                </div>

                {/* RIGHT COLUMN: The File Upload Region (7 cols) */}
                <div className="md:col-span-7 flex flex-col items-stretch">
                    {!overrideSelectedDomain && (
                        <div className="mb-4">
                            <Label className="text-xs font-semibold text-slate-700 mb-1.5 inline-block">Target Domain</Label>
                            <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                                <SelectItem value="" disabled>Select a domain...</SelectItem>
                                {domains.map(d => (
                                    <SelectItem key={d.id} value={d.id.toString()}>{d.name}</SelectItem>
                                ))}
                            </Select>
                        </div>
                    )}
                    
                    <Label className="text-xs font-semibold text-slate-700 mb-1.5 inline-block">Select File</Label>
                    <div className={`flex-1 border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-all relative overflow-hidden min-h-[220px] ${file ? 'border-primary/40 bg-primary/5' : 'border-slate-300 bg-slate-50/50 hover:bg-slate-100 hover:border-primary/30 cursor-pointer group'}`}>
                        {file ? (
                            <div className="flex flex-col items-center animate-in zoom-in-95 duration-200">
                                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4 text-primary">
                                    <DynamicIcon name="FileText" className="w-7 h-7" />
                                </div>
                                <h3 className="text-sm font-semibold text-slate-900 break-all max-w-full px-4">{file.name}</h3>
                                <p className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="mt-4 text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-8"
                                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                                >
                                    <DynamicIcon name="Trash2" className="w-3.5 h-3.5 mr-1.5" /> Remove file
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="w-14 h-14 rounded-full bg-white shadow-sm border border-slate-200 flex items-center justify-center mb-4 text-slate-400 group-hover:text-primary group-hover:scale-110 transition-all duration-300">
                                    <DynamicIcon name="Upload" className="w-6 h-6" />
                                </div>
                                <p className="text-sm font-medium text-slate-900 mb-1">Click or drag file to upload</p>
                                <p className="text-xs text-slate-500 mt-1">Supports PDF, DOCX, XLSX, CSV</p>
                                <input 
                                    type="file" 
                                    onChange={e => { if (e.target.files.length > 0) setFile(e.target.files[0]); }}
                                    accept=".pdf,.docx,.xlsx,.csv"
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>

            {error && <div className="flex items-center gap-2 text-rose-600 text-sm mt-4 p-3 bg-rose-50 rounded-lg border border-rose-100 animate-in slide-in-from-bottom-2"><DynamicIcon name="AlertCircle" className="w-4 h-4 flex-shrink-0" /> {error}</div>}

            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
                <Button onClick={handleUpload} disabled={uploading || !file || !selectedDomain}>
                    {uploading ? <><DynamicIcon name="Loader2" className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : 'Upload & Preview'}
                </Button>
            </div>
        </div>
    );

    const renderStep2 = () => {
        if (!extracted) return null;
        const { entities = [], metrics = [], rules = [] } = extracted.extracted || {};

        const columnHeader = (iconName, label, count, tone) => {
            const tones = {
                blue: { wrap: 'bg-blue-100', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700', border: 'border-blue-100' },
                green: { wrap: 'bg-emerald-100', icon: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800', border: 'border-emerald-100' },
                purple: { wrap: 'bg-violet-100', icon: 'text-violet-700', badge: 'bg-violet-100 text-violet-800', border: 'border-violet-100' },
            };
            const t = tones[tone] || tones.blue;
            return (
                <div className={`flex shrink-0 items-center gap-2 border-b px-3 py-2.5 ${t.border} bg-white/95`}>
                    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${t.wrap}`}>
                        <DynamicIcon name={iconName} className={`h-3.5 w-3.5 ${t.icon}`} />
                    </div>
                    <h3 className="min-w-0 truncate text-sm font-semibold text-slate-800">{label}</h3>
                    <span className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${t.badge}`}>{count}</span>
                </div>
            );
        };

        return (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
                {skippedDuplicates > 0 && (
                    <div className="flex shrink-0 items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 mx-6 mt-4">
                        <DynamicIcon name="AlertCircle" className="h-4 w-4 shrink-0" />
                        <span>
                            <strong>{skippedDuplicates}</strong> item(s) already exist in this domain and were skipped.
                        </span>
                    </div>
                )}

                <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50/90 shadow-inner mx-6 mt-2 mb-2">
                    <div className="grid min-h-0 flex-1 grid-cols-1 divide-y divide-slate-200 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
                        {/* Entities */}
                        <section className="flex min-h-0 min-w-0 flex-col lg:h-full">
                            {columnHeader('Tag', 'Entities', entities.length, 'blue')}
                            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 [scrollbar-gutter:stable]">
                                <div className="mx-auto flex w-full max-w-full flex-col gap-3">
                                    {entities.length === 0 ? (
                                        <p className="text-xs italic text-slate-500">No entities extracted.</p>
                                    ) : (
                                        entities.map((ent, idx) => (
                                            <div
                                                key={idx}
                                                className="min-w-0 rounded-lg border border-blue-100/90 bg-white p-3 shadow-sm ring-1 ring-blue-50/80"
                                            >
                                                <div className="flex min-w-0 items-start gap-2">
                                                    <Input
                                                        value={ent.name}
                                                        className="h-9 min-w-0 flex-1 text-sm font-medium"
                                                        placeholder="Entity name"
                                                        onChange={e => {
                                                            const updated = [...entities];
                                                            updated[idx] = { ...updated[idx], name: e.target.value };
                                                            setExtracted({
                                                                ...extracted,
                                                                extracted: { ...extracted.extracted, entities: updated },
                                                            });
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const updated = entities.filter((_, i) => i !== idx);
                                                            setExtracted({
                                                                ...extracted,
                                                                extracted: { ...extracted.extracted, entities: updated },
                                                            });
                                                        }}
                                                        className="mt-0.5 shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                                        title="Remove"
                                                    >
                                                        <DynamicIcon name="X" className="h-4 w-4" />
                                                    </button>
                                                </div>
                                                <Textarea
                                                    value={ent.description || ''}
                                                    rows={3}
                                                    placeholder="Description"
                                                    className="mt-2 min-h-[5rem] resize-none text-sm leading-relaxed"
                                                    onChange={e => {
                                                        const updated = [...entities];
                                                        updated[idx] = { ...updated[idx], description: e.target.value };
                                                        setExtracted({
                                                            ...extracted,
                                                            extracted: { ...extracted.extracted, entities: updated },
                                                        });
                                                    }}
                                                />
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* Metrics */}
                        <section className="flex min-h-0 min-w-0 flex-col lg:h-full">
                            {columnHeader('BarChart3', 'Metrics', metrics.length, 'green')}
                            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 [scrollbar-gutter:stable]">
                                <div className="mx-auto flex w-full max-w-full flex-col gap-3">
                                    {metrics.length === 0 ? (
                                        <p className="text-xs italic text-slate-500">No metrics extracted.</p>
                                    ) : (
                                        metrics.map((met, idx) => (
                                            <div
                                                key={idx}
                                                className="min-w-0 rounded-lg border border-emerald-100/90 bg-white p-3 shadow-sm ring-1 ring-emerald-50/80"
                                            >
                                                <div className="flex min-w-0 items-start gap-2">
                                                    <Input
                                                        value={met.name}
                                                        className="h-9 min-w-0 flex-1 text-sm font-medium"
                                                        placeholder="Metric name"
                                                        onChange={e => {
                                                            const updated = [...metrics];
                                                            updated[idx] = { ...updated[idx], name: e.target.value };
                                                            setExtracted({
                                                                ...extracted,
                                                                extracted: { ...extracted.extracted, metrics: updated },
                                                            });
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const updated = metrics.filter((_, i) => i !== idx);
                                                            setExtracted({
                                                                ...extracted,
                                                                extracted: { ...extracted.extracted, metrics: updated },
                                                            });
                                                        }}
                                                        className="mt-0.5 shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                                        title="Remove"
                                                    >
                                                        <DynamicIcon name="X" className="h-4 w-4" />
                                                    </button>
                                                </div>
                                                <Input
                                                    value={met.formula || ''}
                                                    className="mt-2 h-9 min-w-0 font-mono text-xs"
                                                    placeholder="SQL formula (e.g. SUM(amount))"
                                                    onChange={e => {
                                                        const updated = [...metrics];
                                                        updated[idx] = { ...updated[idx], formula: e.target.value };
                                                        setExtracted({
                                                            ...extracted,
                                                            extracted: { ...extracted.extracted, metrics: updated },
                                                        });
                                                    }}
                                                />
                                                <Textarea
                                                    value={met.description || ''}
                                                    rows={3}
                                                    placeholder="What does this metric measure?"
                                                    className="mt-2 min-h-[5rem] resize-none text-sm leading-relaxed"
                                                    onChange={e => {
                                                        const updated = [...metrics];
                                                        updated[idx] = { ...updated[idx], description: e.target.value };
                                                        setExtracted({
                                                            ...extracted,
                                                            extracted: { ...extracted.extracted, metrics: updated },
                                                        });
                                                    }}
                                                />
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* Rules */}
                        <section className="flex min-h-0 min-w-0 flex-col lg:h-full">
                            {columnHeader('ShieldCheck', 'Rules', rules.length, 'purple')}
                            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 [scrollbar-gutter:stable]">
                                <div className="mx-auto flex w-full max-w-full flex-col gap-3">
                                    {rules.length === 0 ? (
                                        <p className="text-xs italic text-slate-500">No business rules extracted from this document.</p>
                                    ) : (
                                        rules.map((rule, idx) => (
                                            <div
                                                key={idx}
                                                className="min-w-0 rounded-lg border border-violet-100/90 bg-white p-3 shadow-sm ring-1 ring-violet-50/80"
                                            >
                                                <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                                                    <Input
                                                        value={rule.name || ''}
                                                        className="h-9 min-w-0 flex-1 text-sm font-medium"
                                                        placeholder="Rule name"
                                                        onChange={e => {
                                                            const updated = [...rules];
                                                            updated[idx] = { ...updated[idx], name: e.target.value };
                                                            setExtracted({
                                                                ...extracted,
                                                                extracted: { ...extracted.extracted, rules: updated },
                                                            });
                                                        }}
                                                    />
                                                    <div className="flex shrink-0 items-center gap-2 sm:w-auto">
                                                        <Select
                                                            value={rule.priority || 'medium'}
                                                            onValueChange={val => {
                                                                const updated = [...rules];
                                                                updated[idx] = { ...updated[idx], priority: val };
                                                                setExtracted({
                                                                    ...extracted,
                                                                    extracted: { ...extracted.extracted, rules: updated },
                                                                });
                                                            }}
                                                            className="w-full min-w-[7.5rem] sm:w-28 [&_button]:h-9 [&_button]:py-1.5 [&_button]:text-xs"
                                                        >
                                                            <SelectItem value="low">Low</SelectItem>
                                                            <SelectItem value="medium">Medium</SelectItem>
                                                            <SelectItem value="high">High</SelectItem>
                                                        </Select>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const updated = rules.filter((_, i) => i !== idx);
                                                                setExtracted({
                                                                    ...extracted,
                                                                    extracted: { ...extracted.extracted, rules: updated },
                                                                });
                                                            }}
                                                            className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                                            title="Remove"
                                                        >
                                                            <DynamicIcon name="X" className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <Textarea
                                                    value={rule.rule || ''}
                                                    rows={3}
                                                    placeholder="Rule logic / condition"
                                                    className="mt-2 min-h-[5rem] resize-none text-sm leading-relaxed"
                                                    onChange={e => {
                                                        const updated = [...rules];
                                                        updated[idx] = { ...updated[idx], rule: e.target.value };
                                                        setExtracted({
                                                            ...extracted,
                                                            extracted: { ...extracted.extracted, rules: updated },
                                                        });
                                                    }}
                                                />
                                                <Textarea
                                                    value={rule.description || ''}
                                                    rows={3}
                                                    placeholder="Why does this rule matter?"
                                                    className="mt-2 min-h-[5rem] resize-none text-sm leading-relaxed"
                                                    onChange={e => {
                                                        const updated = [...rules];
                                                        updated[idx] = { ...updated[idx], description: e.target.value };
                                                        setExtracted({
                                                            ...extracted,
                                                            extracted: { ...extracted.extracted, rules: updated },
                                                        });
                                                    }}
                                                />
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </section>
                    </div>
                </div>

                {error && (
                    <div className="shrink-0 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 mx-6 mb-2">{error}</div>
                )}

                <div className="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
                    <Button variant="outline" onClick={() => setStep(1)} disabled={uploading}>
                        Back
                    </Button>
                    <Button onClick={handleConfirm} disabled={uploading}>
                        {uploading ? 'Saving...' : 'Confirm & Add to Domain'}
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={onClose}
            panelClassName={
                step === 2
                    ? 'max-w-7xl w-[95vw] h-[92vh] max-h-[92vh] flex flex-col overflow-hidden'
                    : 'max-w-3xl'
            }
        >
            <DialogContent
            className={
                step === 2
                    ? 'flex min-h-0 flex-1 flex-col gap-0 overflow-hidden p-0'
                    : 'max-w-3xl'
            }
        >
            <DialogHeader
                className={
                    step === 2
                        ? 'mb-0 shrink-0 space-y-1.5 border-b border-slate-100 px-6 pb-4 pt-6 text-left'
                        : 'px-6 pt-6 text-left'
                }
            >
                <DialogTitle>Upload Document</DialogTitle>
                <DialogDescription>
                    {step === 1
                        ? 'Upload a document to extract semantic entities, metrics, and business rules.'
                        : 'Review and edit extracted knowledge before saving. Remove items you do not need.'}
                </DialogDescription>
            </DialogHeader>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {step === 2 ? (
                    renderStep2()
                ) : (
                    <div className="p-6">
                        {renderStep1()}
                    </div>
                )}
            </div>
        </DialogContent>
        </Dialog>
    );
};


const SemanticKnowledgeBasePage = () => {
    const { token, user } = useAuth();
    const { formatDate } = useTimezoneFormatter();
    
    // Core state
    const [domains, setDomains] = useState([]);
    const [metrics, setMetrics] = useState([]);
    const [rules, setRules] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [domainSearch, setDomainSearch] = useState('');
    const [domainSortOption, setDomainSortOption] = useState('updated_desc');
    
    // View state
    const [activeDomainObj, setActiveDomainObj] = useState(null);
    const [activeDetailTab, setActiveDetailTab] = useState('metrics');
    
    // Modals state
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [createType, setCreateType] = useState('domain'); // domain, metric, rule
    const [formData, setFormData] = useState({
        name: '', description: '', keywords: '', status: 'active', version: '1.0', synonyms: '', definition: '', rule: '', priority: 'medium', domain_id: ''
    });
    
    const [uploadModalOpen, setUploadModalOpen] = useState(false); // Document Upload
    
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteItem, setDeleteItem] = useState(null);
    const [deleteItemType, setDeleteItemType] = useState('');
    const [domainInUseData, setDomainInUseData] = useState(null);
    
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [editItemType, setEditItemType] = useState('');

    const [aiGenerateModalOpen, setAiGenerateModalOpen] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiModalError, setAiModalError] = useState(null);
    const [generating, setGenerating] = useState(false);

    // JSON Import state
    const [importJsonModalOpen, setImportJsonModalOpen] = useState(false);
    const [importJsonFile, setImportJsonFile] = useState(null);
    const [importJsonParsed, setImportJsonParsed] = useState(null);
    const [importJsonTargetDomain, setImportJsonTargetDomain] = useState('');
    const [importJsonLoading, setImportJsonLoading] = useState(false);
    const [importJsonError, setImportJsonError] = useState(null);
    const sortedDomains = useMemo(() => {
        const list = [...domains].filter((d) =>
            (d.name || '').toLowerCase().includes(domainSearch.toLowerCase()) ||
            (d.description || '').toLowerCase().includes(domainSearch.toLowerCase())
        );
        list.sort((a, b) => {
            const aUpdated = new Date(a.updated_at || a.created_at || 0).getTime();
            const bUpdated = new Date(b.updated_at || b.created_at || 0).getTime();
            if (domainSortOption === 'updated_desc') return bUpdated - aUpdated;
            if (domainSortOption === 'updated_asc') return aUpdated - bUpdated;
            if (domainSortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
            if (domainSortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
            return 0;
        });
        return list;
    }, [domains, domainSearch, domainSortOption]);
    
    const showSuccess = (message) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(null), 3000);
    };

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [domainsRes, metricsRes, rulesRes, documentsRes] = await Promise.all([
                apiFetch('/semantic-kb/domains', token),
                apiFetch('/semantic-kb/metrics', token),
                apiFetch('/semantic-kb/rules', token),
                apiFetch('/semantic-kb/documents', token).catch(() => [])
            ]);
            
            let domainsData = domainsRes?.domains || (Array.isArray(domainsRes) ? domainsRes : []);
            domainsData = domainsData.map(d => ({
                id: d.id, name: d.name, description: d.description, keywords: '', status: d.is_active ? 'active' : 'inactive', version: '1.0'
            }));
            
            setDomains(domainsData);
            setMetrics(Array.isArray(metricsRes) ? metricsRes : []);
            setRules(Array.isArray(rulesRes) ? rulesRes : []);
            setDocuments(Array.isArray(documentsRes) ? documentsRes : []);
            
            // If we are currently viewing a domain, refresh its object reference
            if (activeDomainObj) {
                const refreshedDomain = domainsData.find(d => d.id === activeDomainObj.id);
                if (refreshedDomain) setActiveDomainObj(refreshedDomain);
                else setActiveDomainObj(null); // It got deleted
            }
        } catch (err) {
            setError('Failed to load Knowledge base data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        try {
            let endpoint = `/semantic-kb/${createType}s`;
            let payload = { ...formData };
            if (createType === 'domain') {
                endpoint = `/semantic-kb/domains`;
                payload = { name: formData.name, description: formData.description };
            } else if (createType === 'metric') {
                payload = {
                    name: formData.name, description: formData.description, definition: formData.definition,
                    domain_id: formData.domain_id || activeDomainObj?.id, keywords: formData.keywords
                };
            } else if (createType === 'rule') {
                payload = {
                    name: formData.name, description: formData.description, rule: formData.rule,
                    priority: formData.priority, domain_id: formData.domain_id || activeDomainObj?.id, keywords: formData.keywords
                };
            }

            await apiFetch(endpoint, token, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            setIsCreateDialogOpen(false);
            setFormData({ name: '', description: '', keywords: '', status: 'active', version: '1.0', synonyms: '', definition: '', rule: '', priority: 'medium', domain_id: '' });
            fetchData();
            showSuccess(`${createType.charAt(0).toUpperCase() + createType.slice(1)} created successfully!`);
        } catch (err) { setError(err.message || `Failed to create ${createType}.`); }
    };

    const handleDelete = async (forceDelete = false) => {
        if (!deleteItem) return;
        try {
            let endpoint = `/semantic-kb/${deleteItemType}s/${deleteItem.id}`;
            if (deleteItemType === 'domain') endpoint = `/semantic-kb/domains/${deleteItem.id}${forceDelete ? '?force=true' : ''}`;
            await apiFetch(endpoint, token, { method: 'DELETE' });
            fetchData();
            showSuccess(`${deleteItemType.charAt(0).toUpperCase() + deleteItemType.slice(1)} deleted successfully!`);
            closeDeleteModal();
            if (deleteItemType === 'domain' && activeDomainObj?.id === deleteItem.id) setActiveDomainObj(null);
        } catch (err) {
            try {
                const detail = err.detail || (err.message && err.message.startsWith('{') ? JSON.parse(err.message) : null);
                if (detail && (detail.code === 'DOMAIN_IN_USE' || detail.bound_models)) {
                    setDomainInUseData({ domain: deleteItem, boundModels: detail.bound_models || [], message: detail.message });
                    setDeleteModalOpen(false);
                    return;
                }
            } catch (_) { }
            setError(err.message || `Failed to delete ${deleteItemType}.`);
            closeDeleteModal();
        }
    };

    const handleEdit = async (type, id, updatedData) => {
        try {
            let endpoint = `/semantic-kb/${type}s/${id}`;
            if (type === 'domain') endpoint = `/semantic-kb/domains/${id}`;
            await apiFetch(endpoint, token, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updatedData) });
            fetchData();
            showSuccess(`${type.charAt(0).toUpperCase() + type.slice(1)} updated successfully!`);
        } catch (err) { setError('Failed to update item'); }
    };

    const handleDownloadTemplate = async (templateType) => {
        try {
            // Templates use the interactive layer (port 8001) — openpyxl lives there, not the control plane
            const interactiveBase = (import.meta.env.VITE_INTERACTIVE_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1').replace(/\/+$/, '');
            const resp = await fetch(`${interactiveBase}/semantic-kb/templates/${templateType}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!resp.ok) throw new Error("Failed to fetch template");
            const blob = await resp.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${templateType}_import_template.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        } catch(e) {
            setError(`Failed to download ${templateType} template.`);
        }
    };

    const handleExport = async (format) => {
        if (!activeDomainObj) return;
        setExporting(true);
        try {
            const interactiveBase = (import.meta.env.VITE_INTERACTIVE_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1').replace(/\/+$/, '');
            const resp = await fetch(`${interactiveBase}/semantic-kb/domains/${activeDomainObj.id}/export?format=${format}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({ detail: "Failed to export domain" }));
                throw new Error(errData.detail || "Failed to export domain");
            }
            
            const filename = `${activeDomainObj.name.replace(/\s+/g, '_')}_export.${format === 'json' ? 'json' : 'xlsx'}`;
            
            if (format === 'json') {
                const data = await resp.json();
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            } else {
                const blob = await resp.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            }
            showSuccess(`Domain exported as ${format.toUpperCase()} successfully!`);
        } catch (err) {
            setError(err.message || `Failed to export domain as ${format.toUpperCase()}.`);
        } finally {
            setExporting(false);
        }
    };

    // ── JSON Import ─────────────────────────────────────────────────────────────
    const openImportJsonModal = () => {
        setImportJsonFile(null);
        setImportJsonParsed(null);
        setImportJsonTargetDomain(activeDomainObj ? String(activeDomainObj.id) : '');
        setImportJsonError(null);
        setImportJsonModalOpen(true);
    };

    const handleImportJsonFileChange = (file) => {
        setImportJsonFile(file);
        setImportJsonParsed(null);
        setImportJsonError(null);
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const parsed = JSON.parse(ev.target.result);
                setImportJsonParsed(parsed);
            } catch {
                setImportJsonError('Invalid JSON file. Please select a file exported from this Knowledge Base.');
            }
        };
        reader.readAsText(file);
    };

    const handleImportJsonConfirm = async () => {
        if (!importJsonParsed || !importJsonTargetDomain) return;
        setImportJsonLoading(true);
        setImportJsonError(null);
        try {
            const result = await apiFetch(
                `/semantic-kb/domains/${importJsonTargetDomain}/import-json`,
                token,
                { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(importJsonParsed) }
            );
            const parts = [];
            if (result.entities_added > 0) parts.push(`${result.entities_added} entities`);
            if (result.metrics_added  > 0) parts.push(`${result.metrics_added} metrics`);
            if (result.rules_added    > 0) parts.push(`${result.rules_added} rules`);
            const summary = parts.length > 0 ? `Imported: ${parts.join(', ')}.` : 'No new items imported.';
            const dupNote = result.skipped_duplicates > 0 ? ` Skipped ${result.skipped_duplicates} duplicates.` : '';
            showSuccess(summary + dupNote);
            setImportJsonModalOpen(false);
            fetchData();
        } catch (err) {
            setImportJsonError(err.message || 'Import failed. Please try again.');
        } finally {
            setImportJsonLoading(false);
        }
    };
    // ────────────────────────────────────────────────────────────────────────────

    const openDeleteModal = (type, item) => { setDeleteItem(item); setDeleteItemType(type); setDeleteModalOpen(true); };
    const closeDeleteModal = () => { setDeleteModalOpen(false); setDeleteItem(null); setDeleteItemType(''); setDomainInUseData(null); };
    const openEditModal = (type, item) => { setEditItem(item); setEditItemType(type); setEditModalOpen(true); };
    const closeEditModal = () => { setEditModalOpen(false); setEditItem(null); setEditItemType(''); };

    const handleAIGenerate = async () => {
        const trimmed = aiPrompt.trim();
        if (trimmed.length < 10) {
            setAiModalError('Please enter at least 10 characters so AI can understand your domain better and deliver accurate results.');
            return;
        }
        setAiModalError(null);
        setGenerating(true);
        try {
            await apiFetch('/semantic-kb/domains/generate', token, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt: trimmed })
            });
            showSuccess('Domain generated successfully from AI.');
            setAiGenerateModalOpen(false);
            setAiPrompt('');
            setAiModalError(null);
            fetchData();
        } catch(e) {
            // Show error inside the modal, not as a global banner
            const msg = e.message || 'Failed to generate domain using AI. Please try again.';
            setAiModalError(msg);
        } finally {
            setGenerating(false);
        }
    };

    const renderDomainList = () => (
        <div className="space-y-6 animate-in fade-in-50 duration-300">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Knowledge base</h1>
                    <p className="text-gray-600 mt-2">Manage domains, metrics, and rules for AI-powered context</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" className="text-primary border-primary/20 hover:bg-primary/5" onClick={() => setAiGenerateModalOpen(true)}>
                        <DynamicIcon name="Sparkles" className="w-4 h-4 mr-2" />
                        Generate using AI
                    </Button>
                    <Button onClick={() => { setCreateType('domain'); setIsCreateDialogOpen(true); }}>
                        <DynamicIcon name="Plus" className="w-4 h-4 mr-2" />
                        New Domain
                    </Button>
                </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <input
                    value={domainSearch}
                    onChange={(e) => setDomainSearch(e.target.value)}
                    placeholder="Search domains..."
                    className="h-10 min-w-[250px] rounded-md border border-gray-300 bg-white px-3 text-sm"
                />
                <select
                    value={domainSortOption}
                    onChange={(e) => setDomainSortOption(e.target.value)}
                    className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
                >
                    <option value="updated_desc">Recently Updated</option>
                    <option value="updated_asc">Oldest Updated</option>
                    <option value="name_asc">Name A-Z</option>
                    <option value="name_desc">Name Z-A</option>
                </select>
            </div>

            {sortedDomains.length === 0 ? (
                <Card className="flex flex-col items-center justify-center py-20 bg-gray-50/50">
                    <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
                        <DynamicIcon name="Box" className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No Domains Found</h3>
                    <p className="text-gray-500 mb-6 text-center max-w-md">Get started by creating a new domain to organize your metrics, rules, and documents.</p>
                    <Button variant="ghost" onClick={() => { setCreateType('domain'); setIsCreateDialogOpen(true); }} className="gap-2">
                        Create First Domain <DynamicIcon name="ArrowRight" className="w-4 h-4" />
                    </Button>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sortedDomains.map(domain => {
                        const domainMetricsCount = metrics.filter(m => m.domain_id === domain.id).length;
                        const domainRulesCount = rules.filter(r => r.domain_id === domain.id).length;
                        return (
                            <Card 
                                key={domain.id} 
                                className="cursor-pointer group relative overflow-hidden flex flex-col h-full hover:shadow-md transition-shadow"
                                onClick={() => { setActiveDomainObj(domain); setActiveDetailTab('metrics'); }}
                            >
                                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary/80 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                                <div className="p-6 flex-1">
                                    <div className="flex items-start justify-between mb-2">
                                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary transition-colors drop-shadow-sm line-clamp-1">{domain.name}</h3>
                                        <Badge variant={domain.status === 'active' ? 'success' : 'secondary'}>{domain.status}</Badge>
                                    </div>
                                    <p className="text-sm text-gray-600 line-clamp-2 mt-2 h-10 overflow-hidden">{domain.description || 'No description provided.'}</p>
                                </div>
                                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                                    <div className="flex items-center gap-4 text-sm text-gray-600 font-medium">
                                        <span className="flex items-center gap-1.5"><DynamicIcon name="BarChart3" className="w-4 h-4 text-primary"/> {domainMetricsCount} Metrics</span>
                                        <span className="flex items-center gap-1.5"><DynamicIcon name="ShieldCheck" className="w-4 h-4 text-primary/70"/> {domainRulesCount} Rules</span>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white" onClick={(e) => { e.stopPropagation(); openEditModal('domain', domain); }}>
                                            <DynamicIcon name="Pencil" className="w-3.5 h-3.5 text-gray-500" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-red-50 hover:text-red-600 text-gray-500" onClick={(e) => { e.stopPropagation(); openDeleteModal('domain', domain); }}>
                                            <DynamicIcon name="Trash2" className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        )
                    })}
                </div>
            )}
        </div>
    );

    const renderDomainDetail = () => {
        const domainMetrics = metrics.filter(m => m.domain_id === activeDomainObj?.id);
        const domainRules = rules.filter(r => r.domain_id === activeDomainObj?.id);
        const domainDocs = documents.filter(d => d.domain_id === activeDomainObj?.id);

        return (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                {/* Breadcrumbs and Header */}
                <div>
                    <button 
                        onClick={() => setActiveDomainObj(null)} 
                        className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-2 mb-4"
                    >
                        <DynamicIcon name="ArrowLeft" className="w-4 h-4" /> Back to Domains
                    </button>
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-3xl font-bold text-gray-900">{activeDomainObj.name}</h1>
                                <Badge variant={activeDomainObj.status === 'active' ? 'success' : 'secondary'}>{activeDomainObj.status}</Badge>
                            </div>
                            <p className="text-gray-600 mt-2 max-w-3xl">{activeDomainObj.description}</p>
                        </div>
                        <div className="flex gap-2 h-10">
                            {/* Import dropdown — two distinct flows */}
                            <Dropdown
                                trigger={
                                    <Button variant="outline">
                                        <DynamicIcon name="Upload" className="w-4 h-4 mr-2" />
                                        Import
                                        <DynamicIcon name="ChevronDown" className="ml-2 w-3 h-3 opacity-50" />
                                    </Button>
                                }
                            >
                                <DropdownItem onClick={openImportJsonModal}>
                                    <DynamicIcon name="FileJson" className="w-4 h-4 mr-2 text-orange-500" />
                                    <div className="text-left">
                                        <div className="font-medium">Import JSON</div>
                                        <div className="text-xs text-gray-400">Restore from exported KB file</div>
                                    </div>
                                </DropdownItem>
                                <DropdownItem onClick={() => setUploadModalOpen(true)}>
                                    <DynamicIcon name="Sparkles" className="w-4 h-4 mr-2 text-violet-500" />
                                    <div className="text-left">
                                        <div className="font-medium">Extract from Document</div>
                                        <div className="text-xs text-gray-400">AI parses PDF, DOCX, XLSX, CSV</div>
                                    </div>
                                </DropdownItem>
                            </Dropdown>

                            {(user?.permissions?.includes('knowledge_base:export') || user?.role === 'platform_admin') && (
                                <Dropdown
                                    trigger={
                                        <Button variant="outline" disabled={exporting}>
                                            <DynamicIcon name="Download" className="w-4 h-4 mr-2" />
                                            {exporting ? 'Exporting...' : 'Export'}
                                            <DynamicIcon name="ChevronDown" className="ml-2 w-3 h-3 opacity-50" />
                                        </Button>
                                    }
                                >
                                    <DropdownItem onClick={() => handleExport('json')}>
                                        <DynamicIcon name="FileJson" className="w-4 h-4 mr-2 text-orange-500" /> Export as JSON
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleExport('excel')}>
                                        <DynamicIcon name="FileSpreadsheet" className="w-4 h-4 mr-2 text-green-600" /> Export as Excel
                                    </DropdownItem>
                                </Dropdown>
                            )}
                            <Button variant="outline" onClick={() => openEditModal('domain', activeDomainObj)}>
                                <DynamicIcon name="Pencil" className="w-4 h-4 mr-2" /> Edit Domain
                            </Button>
                            <Button variant="outline" className="text-red-600 hover:bg-red-50 hover:border-red-200" onClick={() => openDeleteModal('domain', activeDomainObj)}>
                                <DynamicIcon name="Trash2" className="w-4 h-4 mr-2" /> Delete
                            </Button>
                        </div>
                    </div>
                </div>

                <Tabs value={activeDetailTab} onValueChange={setActiveDetailTab}>
                    <TabsList>
                        <TabsTrigger value="metrics">
                            <DynamicIcon name="BarChart3" className="w-4 h-4 mr-2" /> Metrics
                            <Badge variant="secondary" className="ml-2 px-2 py-0.5">{domainMetrics.length}</Badge>
                        </TabsTrigger>
                        <TabsTrigger value="rules">
                            <DynamicIcon name="ShieldCheck" className="w-4 h-4 mr-2" /> Rules
                            <Badge variant="secondary" className="ml-2 px-2 py-0.5">{domainRules.length}</Badge>
                        </TabsTrigger>
                        <TabsTrigger value="documents">
                            <DynamicIcon name="Files" className="w-4 h-4 mr-2" /> Documents
                            <Badge variant="secondary" className="ml-2 px-2 py-0.5">{domainDocs.length}</Badge>
                        </TabsTrigger>
                    </TabsList>

                    {/* METRICS TAB */}
                    <TabsContent value="metrics" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg">Domain Metrics</CardTitle>
                                    <Button onClick={() => { setCreateType('metric'); setFormData(prev => ({...prev, domain_id: activeDomainObj.id})); setIsCreateDialogOpen(true); }}>
                                        <DynamicIcon name="Plus" className="w-4 h-4 mr-2" /> Add Metric
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Description</TableHead>
                                            <TableHead>Formula</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {domainMetrics.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} className="text-center py-12 text-gray-500">
                                                    No metrics defined for this domain. Click 'Add Metric' to create one.
                                                </TableCell>
                                            </TableRow>
                                        ) : domainMetrics.map(metric => (
                                            <TableRow key={metric.id}>
                                                <TableCell className="font-medium text-gray-900">{metric.name}</TableCell>
                                                <TableCell className="text-gray-600 max-w-sm"><p className="line-clamp-2">{metric.description}</p></TableCell>
                                                <TableCell className="font-mono text-xs text-primary bg-primary/10 rounded px-2">{metric.definition}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Button variant="ghost" size="icon" onClick={() => openEditModal('metric', metric)}>
                                                            <DynamicIcon name="Pencil" className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => openDeleteModal('metric', metric)}>
                                                            <DynamicIcon name="Trash2" className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* RULES TAB */}
                    <TabsContent value="rules" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg">Business Rules</CardTitle>
                                    <Button onClick={() => { setCreateType('rule'); setFormData(prev => ({...prev, domain_id: activeDomainObj.id})); setIsCreateDialogOpen(true); }}>
                                        <DynamicIcon name="Plus" className="w-4 h-4 mr-2" /> Add Rule
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Rule Name</TableHead>
                                            <TableHead>Logic</TableHead>
                                            <TableHead>Description</TableHead>
                                            <TableHead>Priority</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {domainRules.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} className="text-center py-12 text-gray-500">
                                                    No rules defined for this domain. Click 'Add Rule' to create one.
                                                </TableCell>
                                            </TableRow>
                                        ) : domainRules.map(rule => {
                                            // Safety-net: normalise numeric priority from older records
                                            const priorityLabel = typeof rule.priority === 'number'
                                                ? (rule.priority >= 8 ? 'high' : rule.priority >= 4 ? 'medium' : 'low')
                                                : (rule.priority || 'medium');
                                            const priorityVariant = priorityLabel === 'high' ? 'destructive' : priorityLabel === 'medium' ? 'default' : 'secondary';
                                            return (
                                            <TableRow key={rule.id}>
                                                <TableCell className="font-medium text-gray-900">{rule.name}</TableCell>
                                                <TableCell className="font-mono text-xs text-primary bg-primary/10 rounded px-2 max-w-xs">{rule.rule}</TableCell>
                                                <TableCell className="text-gray-600 max-w-sm"><p className="line-clamp-2">{rule.description}</p></TableCell>
                                                <TableCell>
                                                    <Badge variant={priorityVariant}>
                                                        {priorityLabel.charAt(0).toUpperCase() + priorityLabel.slice(1)}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Button variant="ghost" size="icon" onClick={() => openEditModal('rule', rule)}>
                                                            <DynamicIcon name="Pencil" className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => openDeleteModal('rule', rule)}>
                                                            <DynamicIcon name="Trash2" className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* DOCUMENTS TAB */}
                    <TabsContent value="documents" className="space-y-4">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between py-4">
                                <CardTitle className="text-lg">Source Documents</CardTitle>
                                <Button onClick={() => setUploadModalOpen(true)}>
                                    <DynamicIcon name="Upload" className="w-4 h-4 mr-2" /> Upload Document
                                </Button>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Filename</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Rows Extracted</TableHead>
                                            <TableHead>Date Uploaded</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {domainDocs.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} className="text-center py-12 text-gray-500">
                                                    No documents uploaded to this domain yet.
                                                </TableCell>
                                            </TableRow>
                                        ) : domainDocs.map(doc => (
                                            <TableRow key={doc.id}>
                                                <TableCell className="font-medium text-gray-900 flex items-center gap-2">
                                                    <DynamicIcon name={doc.file_type === 'xlsx' || doc.file_type === 'csv' ? 'Table' : 'FileText'} className="w-4 h-4 text-gray-400" />
                                                    {doc.filename}
                                                </TableCell>
                                                <TableCell className="text-gray-600 uppercase font-mono text-xs">{doc.file_type}</TableCell>
                                                <TableCell>{doc.row_count > 0 ? doc.row_count : '-'}</TableCell>
                                                <TableCell className="text-gray-500 text-sm">
                                                    {formatDate(doc.created_at, { month: 'short', day: '2-digit', year: 'numeric' })}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <LoadingSpinner />
                <span className="ml-3 text-gray-500 font-medium">Loading Knowledge base...</span>
            </div>
        );
    }

    return (
        <div className="px-6 py-6 w-full min-h-screen bg-gray-50/30">
            {successMessage && (
                <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
                    <Alert variant="success" className="shadow-lg border-0 bg-white">
                        <DynamicIcon name="CheckCircle" className="h-5 w-5 text-green-600" />
                        <AlertDescription>{successMessage}</AlertDescription>
                    </Alert>
                </div>
            )}
            {error && (
                <Alert variant="destructive" className="mb-6 shadow-sm bg-white">
                    <DynamicIcon name="AlertCircle" className="h-5 w-5" />
                    <AlertDescription>{error}</AlertDescription>
                    <button onClick={() => setError(null)} className="ml-auto text-sm font-medium text-red-600 hover:text-red-800 focus:outline-none">
                        Dismiss
                    </button>
                </Alert>
            )}

            {/* Main View Router */}
            {activeDomainObj === null ? renderDomainList() : renderDomainDetail()}

            {/* Create Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create New {createType.charAt(0).toUpperCase() + createType.slice(1)}</DialogTitle>
                        <DialogDescription>Add a new {createType} to your knowledge base.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="name">Name</Label>
                            <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Enter name" />
                        </div>
                        <div>
                            <Label htmlFor="description">Description</Label>
                            <Textarea id="description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Enter description" />
                        </div>

                        {createType === 'metric' && (
                            <>
                                <div><Label htmlFor="definition">Definition/Formula</Label><Textarea id="definition" value={formData.definition || ''} onChange={(e) => setFormData({...formData, definition: e.target.value})} placeholder="e.g. SUM(payment_amount)" /></div>
                                {activeDomainObj === null && (
                                    <div><Label>Domain</Label><Select value={formData.domain_id || (domains[0]?.id?.toString() || '')} onValueChange={(val) => setFormData({...formData, domain_id: val})}>
                                        {domains.map(d => <SelectItem key={d.id} value={d.id.toString()}>{d.name}</SelectItem>)}
                                    </Select></div>
                                )}
                            </>
                        )}
                        {createType === 'rule' && (
                            <>
                                <div><Label htmlFor="rule">Rule Logic</Label><Textarea id="rule" value={formData.rule || ''} onChange={(e) => setFormData({...formData, rule: e.target.value})} placeholder="e.g. If denied claims > 100..." /></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><Label>Priority</Label><Select value={formData.priority || 'medium'} onValueChange={(val) => setFormData({...formData, priority: val})}>
                                        <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem>
                                    </Select></div>
                                    {activeDomainObj === null && (
                                        <div><Label>Domain</Label><Select value={formData.domain_id || (domains[0]?.id?.toString() || '')} onValueChange={(val) => setFormData({...formData, domain_id: val})}>
                                            {domains.map(d => <SelectItem key={d.id} value={d.id.toString()}>{d.name}</SelectItem>)}
                                        </Select></div>
                                    )}
                                </div>
                            </>
                        )}
                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleCreate} disabled={!formData.name}><DynamicIcon name="Plus" className="w-4 h-4 mr-2" />Create</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* AI Generate Dialog */}
            <Dialog open={aiGenerateModalOpen} onOpenChange={(open) => { setAiGenerateModalOpen(open); if (!open) { setAiPrompt(''); setAiModalError(null); } }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><DynamicIcon name="Sparkles" className="w-5 h-5 text-primary" /> Generate Domain AI</DialogTitle>
                        <DialogDescription>Let AI build a foundational domain structure (Metrics and Rules) based on your description.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Inline error display inside modal */}
                        {aiModalError && (
                            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                                <DynamicIcon name="AlertCircle" className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-500" />
                                <span>{aiModalError}</span>
                            </div>
                        )}
                        <div>
                            <Label className="mb-1 block">Domain Description</Label>
                            <Textarea 
                                rows={4}
                                value={aiPrompt}
                                onChange={(e) => { setAiPrompt(e.target.value); if (aiModalError) setAiModalError(null); }}
                                placeholder="e.g. Revenue cycle management in healthcare covering claims, adjustments, and physician payments..."
                                className={aiPrompt.trim().length > 0 && aiPrompt.trim().length < 10 ? 'border-amber-400 focus:ring-amber-400' : ''}
                            />
                            <div className="flex items-center justify-between mt-1">
                                {aiPrompt.trim().length > 0 && aiPrompt.trim().length < 10 ? (
                                    <p className="text-xs text-amber-600 flex items-center gap-1">
                                        <DynamicIcon name="Info" className="w-3 h-3" />
                                        Add at least {10 - aiPrompt.trim().length} more character{10 - aiPrompt.trim().length !== 1 ? 's' : ''} so AI can understand better
                                    </p>
                                ) : <span />}
                                <span className={`text-xs ml-auto ${aiPrompt.trim().length < 10 ? 'text-gray-400' : 'text-green-600'}`}>
                                    {aiPrompt.trim().length} chars
                                </span>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button variant="outline" onClick={() => { setAiGenerateModalOpen(false); setAiPrompt(''); setAiModalError(null); }}>Cancel</Button>
                            <Button onClick={handleAIGenerate} disabled={generating || aiPrompt.trim().length < 10} className="bg-primary hover:opacity-90">
                                {generating ? <><DynamicIcon name="Loader2" className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><DynamicIcon name="Sparkles" className="w-4 h-4 mr-2" />Generate Context</>}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <DeleteConfirmationModal isOpen={deleteModalOpen} onClose={closeDeleteModal} onConfirm={() => handleDelete(false)} itemName={deleteItem?.name || ''} itemType={deleteItemType} />

            {/* Domain-In-Use Force Delete Confirmation */}
            {domainInUseData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDomainInUseData(null)} />
                    <div className="relative z-50 w-full max-w-md rounded-xl bg-white shadow-2xl p-6">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                <DynamicIcon name="AlertTriangle" className="w-6 h-6 text-amber-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-gray-900 mb-1">Domain Linked to Data Models</h3>
                                <p className="text-sm text-gray-600 mb-3">{domainInUseData.message}</p>
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                                    <p className="text-sm font-medium text-amber-800 mb-1">Linked data models:</p>
                                    <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
                                        {domainInUseData.boundModels.map((name, i) => <li key={i}>{name}</li>)}
                                    </ul>
                                </div>
                                <p className="text-sm text-gray-500 mb-4">Force-deleting will unbind all linked models and permanently remove this domain and all its knowledge.</p>
                                <div className="flex gap-3">
                                    <Button variant="outline" onClick={() => setDomainInUseData(null)} className="flex-1">Cancel</Button>
                                    <Button variant="destructive" className="flex-1" onClick={() => { setDomainInUseData(null); handleDelete(true); }}>
                                        <DynamicIcon name="Trash2" className="w-4 h-4 mr-2" /> Force Delete
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <EditModal isOpen={editModalOpen} onClose={closeEditModal} onSave={handleEdit} item={editItem} itemType={editItemType} domains={domains} />

            <DocumentImportModal
                isOpen={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                domains={domains}
                overrideSelectedDomain={activeDomainObj?.id || ''}
                onDownloadTemplate={handleDownloadTemplate}
                onSuccess={(msg) => { showSuccess(msg); fetchData(); }}
                token={token}
            />

            {/* ── JSON Import Modal ──────────────────────────────────────────── */}
            {importJsonModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setImportJsonModalOpen(false)} />
                    <div className="relative z-50 w-full max-w-lg rounded-xl bg-white shadow-2xl animate-in fade-in-50 zoom-in-95 duration-200">
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                    <DynamicIcon name="Upload" className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Import Knowledge Base JSON</h2>
                                    <p className="text-sm text-gray-500">Import a previously exported JSON file into a domain.</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <Label className="text-xs font-semibold text-slate-700 mb-1.5 inline-block">Target Domain</Label>
                                    <Select value={importJsonTargetDomain} onValueChange={setImportJsonTargetDomain}>
                                        <SelectItem value="" disabled>Select a domain...</SelectItem>
                                        {domains.map(d => (
                                            <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
                                        ))}
                                    </Select>
                                </div>

                                <div>
                                    <Label className="text-xs font-semibold text-slate-700 mb-1.5 inline-block">JSON File</Label>
                                    <div className={`relative border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center text-center transition-all min-h-[110px] ${importJsonFile ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-blue-300 cursor-pointer'}`}>
                                        {importJsonFile ? (
                                            <div className="flex items-center gap-3 w-full">
                                                <DynamicIcon name="FileJson" className="w-8 h-8 text-orange-500 flex-shrink-0" />
                                                <div className="text-left flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-gray-900 truncate">{importJsonFile.name}</p>
                                                    <p className="text-xs text-gray-500">{(importJsonFile.size / 1024).toFixed(1)} KB</p>
                                                </div>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); handleImportJsonFileChange(null); }} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0">
                                                    <DynamicIcon name="X" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <DynamicIcon name="FileJson" className="w-8 h-8 text-gray-300 mb-2" />
                                                <p className="text-sm text-gray-500">Click to select a <strong>.json</strong> export file</p>
                                                <input type="file" accept=".json,application/json" onChange={e => { if (e.target.files?.[0]) handleImportJsonFileChange(e.target.files[0]); }} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                            </>
                                        )}
                                    </div>
                                </div>

                                {importJsonParsed && !importJsonError && (
                                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                                        <p className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                                            <DynamicIcon name="Info" className="w-4 h-4" /> Preview — items found in this file:
                                        </p>
                                        <div className="grid grid-cols-3 gap-3 text-center">
                                            <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
                                                <p className="text-2xl font-bold text-blue-700">{(importJsonParsed.entities || []).length}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">Entities</p>
                                            </div>
                                            <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
                                                <p className="text-2xl font-bold text-emerald-700">{(importJsonParsed.metrics || []).length}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">Metrics</p>
                                            </div>
                                            <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
                                                <p className="text-2xl font-bold text-violet-700">{(importJsonParsed.rules || []).length}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">Rules</p>
                                            </div>
                                        </div>
                                        {importJsonParsed.domain?.name && (
                                            <p className="text-xs text-blue-600 mt-2">Source domain: <strong>{importJsonParsed.domain.name}</strong></p>
                                        )}
                                        <p className="text-xs text-gray-400 mt-1">Duplicates will be automatically skipped.</p>
                                    </div>
                                )}

                                {importJsonError && (
                                    <div className="flex items-center gap-2 text-rose-600 text-sm p-3 bg-rose-50 rounded-lg border border-rose-100">
                                        <DynamicIcon name="AlertCircle" className="w-4 h-4 flex-shrink-0" />
                                        {importJsonError}
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3 mt-6 pt-5 border-t border-gray-100">
                                <Button variant="outline" onClick={() => setImportJsonModalOpen(false)} disabled={importJsonLoading} className="flex-1">Cancel</Button>
                                <Button onClick={handleImportJsonConfirm} disabled={importJsonLoading || !importJsonParsed || !importJsonTargetDomain || !!importJsonError} className="flex-1">
                                    {importJsonLoading
                                        ? <><DynamicIcon name="Loader2" className="w-4 h-4 mr-2 animate-spin" />Importing...</>
                                        : <><DynamicIcon name="Upload" className="w-4 h-4 mr-2" />Import</>
                                    }
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SemanticKnowledgeBasePage;
